import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { handleServiceError } from '@/utils/errors/errorHandler';

// Stale time configurations (when data is considered fresh)
export const STALE_TIMES = {
  RECIPES: 5 * 60 * 1000,       // 5 minutes - balanced freshness
  INGREDIENTS: 5 * 60 * 1000,   // 5 minutes - prices update daily
  INVENTORY: 1 * 60 * 1000,     // 1 minute - real-time important
  USER_ROLES: 10 * 60 * 1000,   // 10 minutes - rarely change
  ORDERS: 2 * 60 * 1000,        // 2 minutes - moderate importance
  AUDIT_LOGS: 5 * 60 * 1000,    // 5 minutes - historical data
  TEMPLATES: 30 * 60 * 1000,    // 30 minutes - rarely change
} as const;

// Cache time configurations (when unused data is garbage collected)
export const CACHE_TIMES = {
  RECIPES: 60 * 60 * 1000,      // 1 hour
  INGREDIENTS: 30 * 60 * 1000,  // 30 minutes
  INVENTORY: 5 * 60 * 1000,     // 5 minutes
  DEFAULT: 10 * 60 * 1000,      // 10 minutes
} as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIMES.RECIPES,
      gcTime: CACHE_TIMES.DEFAULT,
      refetchOnWindowFocus: false, // Only refetch when explicitly needed
      refetchOnReconnect: 'always', // Refetch on reconnect is good for fresh data
      refetchOnMount: false, // Use cache if data is still fresh
      retry: 2, // Reduce retry attempts
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Request deduplication is enabled by default in React Query
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

// Create persister for offline support with stale-while-revalidate strategy
export const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'camp-menu-cache-v2', // Bump version for new cache strategy
  serialize: JSON.stringify,
  deserialize: JSON.parse,
  throttleTime: 2000, // Reduce write frequency to improve performance
});
